import React, { useCallback, Fragment } from 'react';
import { rem } from 'polished';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { baggage } from 'roo-ui/assets';
import { Flex, Icon, Image, Link, Text, Box } from 'roo-ui/components';
import theme from 'lib/theme';
import { useDataLayer } from 'hooks/useDataLayer';
import { getQffAuth } from 'lib/qffAuth';
import { getIsAuthenticated, getHasDismissedLoginTooltip } from 'store/user/userSelectors';
import { getDeviceType } from 'store/userEnvironment/userEnvironmentSelectors';
import { getPathName } from 'store/router/routerSelectors/routerGetters';
import { dismissLoginTooltip } from 'store/user/userActions';
import { ArrowRight, ArrowUp, Card } from './primitives';

const login = async () => {
  try {
    const qffAuth = await getQffAuth();
    qffAuth.showLoginModal();
  } catch (e) {
    //do nothing yet
  }
};

const LoginTooltip = ({ isPhone, isDesktop }) => {
  const { emitInteractionEvent } = useDataLayer();
  const dispatch = useDispatch();
  const device = useSelector(getDeviceType);

  const hasDismissedLoginTooltip = useSelector(getHasDismissedLoginTooltip);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const showLoginTooltip = !isAuthenticated && !hasDismissedLoginTooltip;

  const pathname = useSelector(getPathName);
  const isSearchList = pathname.match(/^\/search\/list/);
  const isProperties = pathname.match(/^\/properties\/\d+/);
  const isCheckout = pathname.match(/\/checkout/);
  const isPageWithLoginTooltip = isSearchList || isProperties || isCheckout;

  const handleDismiss = useCallback(() => {
    dispatch(dismissLoginTooltip());

    emitInteractionEvent({ type: 'Login tooltip', value: `${device} LoginTooltip Dismissed` });
  }, [device, dispatch, emitInteractionEvent]);

  if (!isPageWithLoginTooltip) return null;

  return (
    <Fragment>
      {showLoginTooltip && (
        <Flex
          width={['100%', '100%', '350px']}
          mt={[-3, -3, 0]}
          mr={[0, 0, -3]}
          zIndex={theme.zIndices.stickyNavigation}
          data-testid="login-tooltip"
          flexDirection={['column', 'column', 'row']}
        >
          {isPhone && <ArrowUp />}
          <Card justifyContent={['space-between', 'space-between', 'center']} alignItems="center">
            <Flex justifyContent="center" alignItems="center">
              <Box display="block" width={rem('86px')}>
                <Image alt={baggage} src={baggage} height={rem('46px')} width="100%" pr={4} />
              </Box>
              <Text display="block" fontSize="base">
                <Link mr={1} underline color="inherit" onClick={login} data-testid="login-link">
                  Login{' '}
                </Link>
                to access incredible deals for members!
              </Text>
            </Flex>

            <Box>
              <Icon name="close" ml={8} mr={[0, 4, 0]} size={18} color="greys.dusty" onClick={handleDismiss} data-testid="close-icon" />
            </Box>
          </Card>
          {isDesktop && <ArrowRight />}
        </Flex>
      )}
    </Fragment>
  );
};

LoginTooltip.propTypes = {
  isPhone: PropTypes.bool,
  isDesktop: PropTypes.bool,
};
LoginTooltip.defaultProps = {
  isPhone: false,
  isDesktop: false,
};

export default LoginTooltip;
